import React, { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TerminalIcon from "@mui/icons-material/Terminal";
import { ToolUseEvent, ToolResultEvent } from "../../../types/ai-event";
import { CasLoader } from "../../brand/CasLoader";

interface ToolCallCardProps {
  event: ToolUseEvent;
  result?: ToolResultEvent;
  done?: boolean;
  // Moves this running call to the background (spec 2026-10-27-claude-native-background-tasks, U2). Given by the chat; the button shows
  // only for a Bash call that has no result yet and was not started in the background.
  onBackground?: (toolUseId: string) => void;
}

const ToolCallCardView: React.FC<ToolCallCardProps> = ({
  event,
  result,
  done,
  onBackground,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const inputSummary =
    event.name === "Bash"
      ? (event.input.command as string)
      : event.name === "Read"
      ? (event.input.file_path as string)
      : event.name === "Edit"
      ? (event.input.file_path as string)
      : JSON.stringify(event.input).slice(0, 80);

  const canBackground =
    !!onBackground && event.name === "Bash" && !(result || done) && event.input.run_in_background !== true;

  return (
    <Box
      data-testid="tool-call-card"
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        overflow: "hidden",
        my: 0.5,
        backgroundColor: theme.palette.background.paper,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          px: 1.5,
          py: 1,
          gap: 1,
          cursor: "pointer",
          "&:hover": { backgroundColor: theme.palette.action.hover },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <TerminalIcon sx={{ fontSize: 16, mt: "2px", color: theme.palette.primary.main }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontWeight: 600,
              color: theme.palette.primary.main,
              lineHeight: 1.2,
            }}
          >
            {event.name}
          </Typography>
          <Typography
            sx={{
              fontFamily: "monospace",
              fontSize: "0.8rem",
              lineHeight: 1.4,
              color: theme.palette.text.primary,
              wordBreak: "break-all",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {inputSummary}
          </Typography>
        </Box>
        {!(result || done) && (
          <CasLoader size={16} label="Tool running" sx={{ mt: "2px" }} />
        )}
        {canBackground && (
          <Button
            size="small"
            variant="outlined"
            data-testid="tool-call-background"
            sx={{ mt: "-2px", py: 0, minWidth: 0, textTransform: "none", fontSize: "0.7rem" }}
            onClick={(e) => {
              e.stopPropagation();
              onBackground?.(event.id);
            }}
          >
            Background
          </Button>
        )}
        {(result || done) && (
          <Typography
            variant="caption"
            sx={{ color: theme.palette.success.main, fontSize: "0.7rem", mt: "2px" }}
          >
            done
          </Typography>
        )}
        <IconButton size="small" sx={{ mt: "-4px" }}>
          {expanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box
          sx={{
            p: 1.5,
            fontFamily: "monospace",
            fontSize: "0.75rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: 300,
            overflow: "auto",
            backgroundColor: theme.palette.background.default,
          }}
        >
          {result ? result.output : done ? "(completed)" : "Running..."}
        </Box>
      </Collapse>
    </Box>
  );
};

// agent-chat-speed Decision 3: re-render only when what the card shows changes — the call (id,
// name, input object), whether it finished, and its output. The expanded state is the card's own.
export const ToolCallCard = React.memo(
  ToolCallCardView,
  (prev, next) =>
    prev.event.id === next.event.id &&
    prev.event.name === next.event.name &&
    prev.event.input === next.event.input &&
    !!prev.result === !!next.result &&
    prev.result?.output === next.result?.output &&
    !!prev.done === !!next.done &&
    prev.onBackground === next.onBackground
);
